import { supabase } from "../lib/supabase";

const API_BASE = process.env.REACT_APP_API_BASE || "";

/**
 * Read one SSE payload from a `data:` line.
 * @param {string} block
 */
function parseSseBlock(block) {
  const line = block.split("\n").find((entry) => entry.startsWith("data: "));
  if (!line) return null;
  try {
    return JSON.parse(line.slice(6));
  } catch {
    return null;
  }
}

/**
 * Signed-in companion fallback (local facts or LLM).
 * Returns `{ text, source }` (`local_facts` | `llm` | miss variants) or throws.
 * `onDelta` receives the text so far while the model is still writing.
 * @param {string} text
 * @param {{ onDelta?: (text: string) => void }} [opts]
 */
export async function fetchCompanionChat(text, { onDelta } = {}) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Bitte melde dich an.");
  const res = await fetch(`${API_BASE}/api/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ text }),
  });
  const type = res.headers.get("content-type") || "";
  if (!res.ok && !type.includes("text/event-stream")) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Antwort fehlgeschlagen (${res.status}).`);
  }
  if (!type.includes("text/event-stream") || !res.body) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `Antwort fehlgeschlagen (${res.status}).`);
    }
    return data;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let streamed = "";
  let result = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const part of parts) {
      const payload = parseSseBlock(part);
      if (!payload) continue;
      if (payload.delta) {
        streamed += payload.delta;
        onDelta?.(streamed);
      }
      if (payload.done) {
        result = { text: payload.text || streamed, source: payload.source || "llm" };
        if (result.text) onDelta?.(result.text);
      }
    }
  }
  if (!result?.text) {
    throw new Error("Antwort fehlgeschlagen.");
  }
  return result;
}
